import React, { useEffect, useRef, useState } from 'react';
import classes from './SearchAndCategories.module.css';
import { isTouchCapable } from '../../hooks/useDragItem';
import { getCategoryIconComponent } from '../../config/categoryIcons';
import CategoryManageSheet from './CategoryManageSheet';

const AllIcon = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
    <path d="M17.5401 8.81063C19.1748 8.81063 20.5001 7.48539 20.5001 5.85062C20.5001 4.21586 19.1748 2.89062 17.5401 2.89062C15.9053 2.89062 14.5801 4.21586 14.5801 5.85062C14.5801 7.48539 15.9053 8.81063 17.5401 8.81063Z" fill="currentColor"/>
    <path d="M6.46 8.81063C8.09476 8.81063 9.42 7.48539 9.42 5.85062C9.42 4.21586 8.09476 2.89062 6.46 2.89062C4.82524 2.89062 3.5 4.21586 3.5 5.85062C3.5 7.48539 4.82524 8.81063 6.46 8.81063Z" fill="currentColor"/>
    <path d="M17.5401 21.1114C19.1748 21.1114 20.5001 19.7862 20.5001 18.1514C20.5001 16.5166 19.1748 15.1914 17.5401 15.1914C15.9053 15.1914 14.5801 16.5166 14.5801 18.1514C14.5801 19.7862 15.9053 21.1114 17.5401 21.1114Z" fill="currentColor"/>
    <path d="M6.46 21.1114C8.09476 21.1114 9.42 19.7862 9.42 18.1514C9.42 16.5166 8.09476 15.1914 6.46 15.1914C4.82524 15.1914 3.5 16.5166 3.5 18.1514C3.5 19.7862 4.82524 21.1114 6.46 21.1114Z" fill="currentColor"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <circle cx="11" cy="11" r="7" stroke="#abacac" strokeWidth="1.8"/>
    <path d="M16.5 16.5L21 21" stroke="#abacac" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
    <path d="M18 6L6 18M6 6L18 18" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round"/>
  </svg>
);

const SearchAndCategories = ({
  categories = [],
  categoryIcons = {},
  activeCategory,
  setActiveCategory,
  onAddItem,
  onAddCategory,
  onRenameCategory,
  onDeleteCategory,
  onSetCategoryIcon,
  onReorderCategories,
  searchQuery,
  setSearchQuery,
}) => {
  const wrapperRef = useRef(null);
  const tabsScrollRef = useRef(null);
  const tabRefs = useRef({});

  const [showManageSheet, setShowManageSheet] = useState(false);

  // Brings the active category tab into view when it changes — e.g. tapping
  // a category chip on Home opens My Gear pre-filtered, but without this
  // the tab strip stays scrolled wherever it was left, so the now-active
  // tab (whichever it is) can be off-screen with nothing visibly selected.
  // "All" is skipped — it's the first tab, always visible at the left edge.
  useEffect(() => {
    if (!activeCategory) return;
    const container = tabsScrollRef.current;
    const tab = tabRefs.current[activeCategory];
    if (!container || !tab) return;
    const targetScrollLeft = tab.offsetLeft - container.offsetWidth / 2 + tab.offsetWidth / 2;
    container.scrollTo({ left: Math.max(0, targetScrollLeft), behavior: 'smooth' });
  }, [activeCategory]);

  // Click-and-drag-to-scroll for the tab strip on a real mouse — touch
  // devices (and Chrome DevTools' device emulation, which reports itself as
  // touch-capable) already get this for free from native touch scrolling,
  // so this only attaches on a genuine mouse to avoid the two fighting each
  // other. Plain overflow-x: auto has no answer for "click and drag" with a
  // mouse; only touch and shift+wheel scroll it without this.
  useEffect(() => {
    if (isTouchCapable) return;
    const el = tabsScrollRef.current;
    if (!el) return;

    let isDown = false;
    let dragged = false;
    let startX = 0;
    let startScrollLeft = 0;

    const onMouseDown = (e) => {
      isDown = true;
      dragged = false;
      startX = e.pageX;
      startScrollLeft = el.scrollLeft;
    };
    const onMouseMove = (e) => {
      if (!isDown) return;
      const delta = e.pageX - startX;
      // A few px of wobble on what was meant to be a plain tap shouldn't
      // count as a drag (and shouldn't suppress the tap's click below).
      if (Math.abs(delta) > 5) dragged = true;
      if (!dragged) return;
      e.preventDefault();
      el.scrollLeft = startScrollLeft - delta;
    };
    const endDrag = () => { isDown = false; };
    // Capture phase, and only once — this needs to run (and stop the click)
    // before the tab button's own onClick handler sees the same event, but
    // only for the click terminating THIS drag, not every future one.
    const onClickCapture = (e) => {
      if (dragged) {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', endDrag);
    el.addEventListener('mouseleave', endDrag);
    el.addEventListener('click', onClickCapture, true);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', endDrag);
      el.removeEventListener('mouseleave', endDrag);
      el.removeEventListener('click', onClickCapture, true);
    };
  }, []);

  return (
    <div className={classes.wrapper} ref={wrapperRef}>

      {/* Search row: full-width input + add button */}
      <div className={classes.searchRow}>
        <div className={classes.searchBar}>
          <span className={classes.searchIcon}><SearchIcon /></span>
          <input
            className={classes.searchInput}
            type="text"
            placeholder="Search gear"
            value={searchQuery || ''}
            onChange={e => setSearchQuery && setSearchQuery(e.target.value)}
            spellCheck="false"
          />
          {searchQuery && (
            <button
              className={classes.searchClearBtn}
              onClick={() => setSearchQuery && setSearchQuery('')}
              aria-label="Clear search"
            >
              <CloseIcon />
            </button>
          )}
        </div>

        <button
          className={classes.addButton}
          onClick={() => onAddItem && onAddItem(activeCategory)}
        >
          <PlusIcon />
          <span className={classes.addButtonLabel}>Add item</span>
        </button>
      </div>

      {/* Category tabs row */}
      <div className={classes.categoryRow}>
        <div className={classes.tabsScroll} ref={tabsScrollRef}>
          <button
            key="all"
            className={`${classes.tab} ${activeCategory === '' ? classes.tabActive : ''}`}
            onClick={() => setActiveCategory && setActiveCategory('')}
          >
            <span className={classes.tabIcon}><AllIcon /></span>
            <span className={classes.tabLabel}>All</span>
            {activeCategory === '' && <span className={classes.tabUnderline} />}
          </button>
          {categories.map((category) => {
            const Icon = getCategoryIconComponent(category, categoryIcons);
            const isActive = activeCategory === category;
            return (
              <button
                key={category}
                ref={el => { tabRefs.current[category] = el; }}
                className={`${classes.tab} ${isActive ? classes.tabActive : ''}`}
                onClick={() => setActiveCategory && setActiveCategory(category)}
              >
                <span className={classes.tabIcon}><Icon /></span>
                <span className={classes.tabLabel}>{category}</span>
                {isActive && <span className={classes.tabUnderline} />}
              </button>
            );
          })}
          <span className={classes.tabDivider} aria-hidden="true" />
          <button
            key="manage"
            className={classes.manageTab}
            onClick={() => setShowManageSheet(true)}
          >
            <span className={classes.manageTabIcon}><i className="ti ti-settings-2" style={{ fontSize: 18 }} /></span>
            <span className={classes.manageTabLabel}>Manage</span>
          </button>
        </div>
      </div>

      {showManageSheet && (
        <CategoryManageSheet
          categories={categories}
          categoryIcons={categoryIcons}
          onClose={() => setShowManageSheet(false)}
          onAddCategory={() => onAddCategory && onAddCategory()}
          onRenameCategory={(oldName, newName) => onRenameCategory && onRenameCategory(oldName, newName)}
          onDeleteCategory={(cat) => onDeleteCategory && onDeleteCategory(cat)}
          onSetCategoryIcon={(cat, key) => onSetCategoryIcon && onSetCategoryIcon(cat, key)}
          onReorderCategories={(cats) => onReorderCategories && onReorderCategories(cats)}
        />
      )}

    </div>
  );
};

export default SearchAndCategories;
